<template>
  <CFormItem :prop="name" :field-value="value">
    <ElSelect
      ref="selectRef"
      v-model="value"
      class="cell-dropdown-select"
      :filterable="hasData"
      :filter-method="filter"
      placeholder=" "
      :disabled="disabled"
      :persistent="false"
      @visible-change="visibleChange"
      @change="valueChange"
      @keydown.stop="noop"
      @click.stop="noop"
    >
      <EditableOptions
        :options-data="optionsData || []"
        :editor="props.params.editor"
        :reload="reload"
      >
        <slot />
      </EditableOptions>
    </ElSelect>
  </CFormItem>
</template>

<script lang="ts" setup>
import { ref, computed, toRef, Ref, nextTick, onMounted } from 'vue';
import { watchOnce } from '@vueuse/core';
import { ICellRendererParams } from 'ag-grid-community';
import { ElSelect } from 'element-plus';
import { pinyinFilterData } from '../../core/CommonInternal';
import { IdLabelList } from '../../core/types';
import EditableOptions from '../../common/EditableOptions.vue';
import { DropdownEditor } from '../../common/DropdownEditor';
import { toInteger, isString, isFunction, noop } from 'lodash-es';
import { useShareData } from '../../core/SharedData';

const props = defineProps<{
  params: ICellRendererParams<any> & {
    fieldName?: string; // 字段名

    /*编辑框是否禁用 */
    disabled?: boolean | string | ((ps: ICellRendererParams<any>) => boolean);

    url?: string; // 通过url接口提供数据源
    transform?: string; // 通过变换提供数据源
    callback?: (
      params: ICellRendererParams<any>,
      abortController: AbortController
    ) => Promise<IdLabelList>; // 通过回调函数提供数据源

    excludes?: number[]; // 从数据源接口中排除特定的ID
    rawId?: boolean; // 保持id原始类型, 默认会转成number

    editor?: DropdownEditor; // 下拉列表编辑器

    valueChange?: (
      api: ICellRendererParams<any>,
      newValue: any,
      oldValue: string
    ) => void;
  };
}>();

// formItem名
// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
const name = props.params.fieldName || props.params.colDef?.field!;

// eslint-disable-next-line vue/no-setup-props-destructure
const tableSetting = useShareData('table', 'setting');
const disabled = computed(() => {
  // table全局禁止edit
  if (tableSetting?.disabledEdit) {
    return true;
  }

  const paramsDisabled = props.params.disabled;
  return isString(paramsDisabled)
  ? !!props.params.data[paramsDisabled]
  : isFunction(paramsDisabled)
  ? paramsDisabled(props.params)
  : paramsDisabled;
});

const callback = (() => {
  if (!props.params.callback) {
    return undefined;
  }

  return async (abortController: AbortController) =>
    await props.params.callback!(props.params, abortController);
})();

const { optionsData, filter, clearFilter, reload, hasData } = pinyinFilterData(
  props.params.url,
  props.params.transform,
  callback,
  undefined,
  true,
  props.params.excludes,
  props.params.rawId
);

let data = ref(props.params.value);
const value = computed({
  get() {
    if (data.value === undefined || data.value === '') {
      return undefined;
    }

    if (!optionsData.value?.length) {
      return undefined;
    }

    let n = props.params.rawId ? data.value : toInteger(data.value);
    // 如果n在列表中未出现, 先中第一条
    // if (!optionsData.value.find((e) => e.id == n)) {
    //   return optionsData.value[0].id;
    // }

    return n;
  },

  set(v: number | string | undefined) {
    data.value = v;
  }
});

async function valueChange(val: number) {
  const old = props.params.value;
  try {
    await props.params.valueChange?.(props.params, val, old);
    props.params.node.setDataValue(name, val, 'form'); // 'form'参数用于区分谁触发的
  } catch (e) {
    value.value = old;
    throw e;
  }
}

// 下拉框变更
function visibleChange(visible: boolean) {
  clearFilter(visible);

  if (!visible) setOptionText();
}

// persistent=false导致text显示成id, 下面手动处理
const selectRef = ref();
let selectedLabel: Ref<string> | undefined;
function setOptionText() {
  if (!selectedLabel) return;

  const id = props.params.value;
  const option = optionsData.value.find((e) => e.id == id);

  nextTick(() => {
    selectedLabel!.value = option?.label || '';
  });
}

onMounted(() => {
  selectedLabel = toRef(selectRef.value, 'selectedLabel');

  if (optionsData.value && optionsData.value.length) setOptionText();
  else watchOnce(optionsData, setOptionText, { deep: true });
});
</script>

<style lang="scss">
.cell-dropdown-select {
  width: unset !important;
  margin-left: 8px;
  margin-right: 8px;
  vertical-align: middle !important;

  &.is-disabled {
    cursor: default !important;

    .el-input__wrapper {
      --el-disabled-bg-color: transparent;
      --el-disabled-text-color: var(--el-input-text-color);

      border-color: transparent !important;
      box-shadow: none !important;

      input {
        cursor: default;
      }
    }
  }

  .el-input__wrapper {
    border: 1px solid #dcdfe655;
    box-shadow: none;
  }

  .el-input__wrapper:hover,
  .el-input__wrapper.is-focus {
    border-color: #dcdfe6;
    box-shadow: none;
  }
}
</style>
